/**
 * Created by rathompson on 5/9/22
 * File: DeviceTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useContext, useMemo, useState, useCallback, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  ConnectedPaginator,
  ConnectedTable,
  ConnectedTableLayout,
  TableProvider
} from '@panwds/react-table'
import {
  COLUMN_MODEL,
  COLUMN_NAME,
  COLUMN_SN,
  COLUMN_ASSOCIATED_APPS,
  COLUMN_ASSET_TYPE,
} from '../../constants/LabelConstants'
import FiltersBar from '../common/table/FiltersBar'
import CellRenderer from '../common/table/cellRenderer/CellRenderer'
import AppsRenderer from '../common/table/cellRenderer/AppsRenderer'
import AppContext from '../../../common/context/AppContext'
import { DEVICE_STATUS_BLACKLIST } from '../../constants/Constants'
import { isEqual } from 'lodash'
import { ARRAY } from '../../../common/utils/Utils'
import V5TableWrapper from '../../../common/components/table/V5TableWrapper'
import DeviceAssetTypeRenderer from '../common/table/cellRenderer/DeviceAssetTypeRenderer'
import { customRowDisableHook } from '../common/table/tableUtil'

const statusBlacklist = DEVICE_STATUS_BLACKLIST

const DeviceTable = ({
  data,
  header,
  loading,
  tableRef: _tableRef,
  shouldHighlightRow,
  setSelected: _setSelected,
  searchCallback
}) => {

  const { getAppName } = useContext(AppContext)

  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(500)
  const totalRecords = useMemo(() => data?.length || 0, [data])
  const [selected, setSelected] = useState([])

  const ref = _tableRef
  const tableRef = useCallback((node) => {
    if (node) {
      ref.current = node
      const _selected = []
      for (const row of node.selectedFlatRows) {
        if (!ARRAY.hasArray(row.original?.associations) || !row.original.associations?.every(({ association_status }) => statusBlacklist.includes(association_status))) {
          _selected.push(row.original)
        }
      }
      if (!isEqual(_selected, selected)) {
        setSelected(_selected)
      }
    }
  }, [ref, selected])

  useEffect(() => {
    if (_setSelected) {
      _setSelected(selected)
    }
  }, [_setSelected, selected])

  const containerRef = useRef()

  const columns = useMemo(() => {
    return [
      {
        accessor: 'serial_number',
        Header: COLUMN_SN,
        width: 250,
        id: 'serial_number',
        Cell: CellRenderer
      },
      {
        accessor: 'device_name',
        Header: COLUMN_NAME,
        width: 150,
        id: 'device_name',
        Cell: CellRenderer
      },
      {
        accessor: 'model',
        Header: COLUMN_MODEL,
        width: 200,
        id: 'model',
        Cell: CellRenderer
      },
      {
        accessor: 'asset_type',
        Header: COLUMN_ASSET_TYPE,
        width: 150,
        id: 'asset_type',
        Cell: DeviceAssetTypeRenderer,
      },
      {
        accessor: 'associations',
        Header: COLUMN_ASSOCIATED_APPS,
        width: 200,
        id: 'associations',
        Cell: AppsRenderer,
        cellToString: ({ row }) => row?.original?.associations?.map(({ app_id }) => getAppName(app_id)).join(' '),
        // cellToString: ({ row }) => row?.original?.associations?.map((association) => getDasAppDisplay(association, apps, getAppName)?.appName).join(' '),
      },
    ]
  }, [getAppName])

  const updateCheckboxes = useCallback((rows) => {
    rows.forEach((data, index) => {
      const tableContainer = containerRef.current
      if (!tableContainer) {
        return rows
      }
      const rowElement = tableContainer.querySelector(`[aria-rowindex="${index}"]`)
      if (rowElement) {
        const label = rowElement.querySelector('label')
        const disabled = data?.disabled || (ARRAY.hasArray(data?.associations) && data?.associations?.every(({ association_status }) => statusBlacklist.includes(association_status)))
        const checkbox = rowElement.querySelector('input[type=checkbox]')
        if (checkbox) {
          checkbox.disabled = disabled
          checkbox.readOnly = disabled
          if (disabled) {
            label.classList.add('inherited-check')
            checkbox.classList.remove('tw-cursor-pointer')
            checkbox.classList.add('tw-cursor-not-allowed')
            checkbox.style.cursor = 'not-allowed'
            checkbox['aria-checked'] = false
            checkbox.checked = false
          }
          else {
            label.classList.remove('inherited-check')
            checkbox.classList.remove('tw-cursor-not-allowed')
            checkbox.classList.add('tw-cursor-pointer')
            checkbox.style.cursor = 'pointer'
          }
        }
      }
    })
    return rows
  }, [])

  const onPaginationChange = useCallback(async ({ pageIndex: page, pageSize: size }) => {
    if (pageIndex !== page || pageSize !== size) {
      setPageIndex(page)
      setPageSize(size)
    }
  }, [pageIndex, pageSize])

  useEffect(() => {
    let index = pageIndex
    let size = pageSize
    while ((index * size) >= totalRecords) {
      --index
      --size
    }
    setPageIndex(index)
    setPageSize(size)
  }, [pageIndex, pageSize, totalRecords])

  const useControlledState = useCallback((state, { instance }) => {
    const selectedRowIds = Object.keys(state.selectedRowIds)
      .map((index) => ({ index, device: instance?.data[Number(index)] }))
      .filter(({ index, device }) => Boolean(device) && (!ARRAY.hasArray(device.associations) || !device?.associations?.every(({ association_status }) => statusBlacklist.includes(association_status))))
      .reduce((map, row) => {
        map[row.index] = true
        return map
      }, {})
    return Object.assign({}, state, { selectedRowIds })
  }, [])
  return (
    <V5TableWrapper className='tw-w-full tw-h-full tw-flex tw-flex-col'>
      <TableProvider
        autoResetPage={false}
        autoResetSelectedRows={false}
        tableInstanceRef={tableRef}
        columns={columns}
        data={data}
        enablePagination
        enableRowSelect
        enableResizeColumns
        enableColumnOrder
        enableColumnSort
        isBackgroundFetching={loading}
        initialState={{
          pageIndex: pageIndex,
          pageSize
        }}
        onPaginationChange={onPaginationChange}
        pageCount={Math.ceil(totalRecords / pageSize)}
        onSelectedRowsChange={() => updateCheckboxes(data)}
        useControlledState={useControlledState}
        overrideHooks={[customRowDisableHook]}
      >
        {Boolean(header) && header}
        <FiltersBar {...{ searchCallback }} />
        <ConnectedTableLayout
          paginator={<ConnectedPaginator pageSizeOptions={[10, 25, 50, 100, 500]} />}
          overflow
        >
          <ConnectedTable
            shouldHighlightRow={shouldHighlightRow}
          />
        </ConnectedTableLayout>
      </TableProvider>
    </V5TableWrapper>
  )
}

DeviceTable.propTypes = {
  header: PropTypes.element,
  data: PropTypes.array.isRequired
}

DeviceTable.defaultProps = {
  data: []
}
export default DeviceTable

